import {
  createFiscalClient,
  requestObjects,
  toRegisters,
} from "@prostocheck/fiscal";
import {
  createKeyBox,
  detectCertificateAuthority,
  fetchCertificates,
  getSigningCertificate,
  importCertificates,
  loadKey,
} from "@prostocheck/kep";
import {
  CA_AUTO_DETECT_VALUE,
  castErrorResult,
  createErrorResult,
  createErrorResultFromException,
  createSuccessResult,
  ERRORS,
  FISCAL_BASE_URL,
  getCmpGatewayUrl,
  getFiscalGatewayUrl,
  IKeyBox,
  ISession,
  SimpleResult,
} from "@prostocheck/shared";

import {
  base64ToBuffer,
  clearPersistedSession,
  IPersistedKeySession,
} from "../persisted-session";
import { IReadKeyRequest, IReadKeyResult } from "./session.models";
import { toKeyOwner } from "./session.utils";

/**
 * Step one of sign-in: decrypt the key container in this tab, pull its
 * certificates from the issuing ЦСК, and report whose key it is. Nothing is
 * sent to ДПС yet — this only answers "is this key usable?".
 *
 * The file bytes and the password never leave the browser. `useLogin` persists
 * them to `localStorage` after a successful sign-in (`savePersistedSession`),
 * not this function — see ai/docs/kep-signing.md.
 */
export const readKey = async ({
  file,
  password,
  cmpUrl,
  authorities,
  onDetectProgress,
}: IReadKeyRequest): Promise<SimpleResult<IReadKeyResult>> => {
  if (!file) {
    return createErrorResult(ERRORS.key.missingFile);
  }

  const keyBox = createKeyBox(getCmpGatewayUrl());

  const loaded = loadKey({
    keyBox,
    file: new Uint8Array(await file.arrayBuffer()),
    password,
  });

  if (!loaded.success) {
    return castErrorResult(loaded);
  }

  const certificates =
    cmpUrl === CA_AUTO_DETECT_VALUE
      ? await detectCertificateAuthority({
          keyBox,
          authorities,
          onProgress: onDetectProgress,
        })
      : await fetchCertificates({ keyBox, cmpUrl });

  if (!certificates.success) {
    return castErrorResult(certificates);
  }

  const signing = getSigningCertificate(keyBox);

  if (!signing.success || !signing.data) {
    return castErrorResult(signing);
  }

  return createSuccessResult({ keyBox, owner: toKeyOwner(signing.data) });
};

/**
 * Step two: sign an `Objects` request with the key. ДПС reads the taxpayer out
 * of the certificate, so this single call both authenticates the user and
 * returns everything they own — see ai/docs/prro-api.md.
 */
export const signIn = async ({
  keyBox,
  owner,
}: IReadKeyResult): Promise<SimpleResult<ISession>> => {
  const client = createFiscalClient({
    keyBox,
    gatewayUrl: getFiscalGatewayUrl(),
    baseUrl: FISCAL_BASE_URL,
  });

  const objects = await requestObjects(client);

  if (!objects.success) {
    return castErrorResult(objects);
  }

  const registers = toRegisters(objects.data ?? null);

  if (registers.length === 0) {
    return createErrorResult(ERRORS.fiscal.noRegisters);
  }

  return createSuccessResult({
    keyBox,
    client,
    owner,
    registers,
    startedAt: new Date().toISOString(),
  });
};

/**
 * Rebuilds a key box from a persisted session — no password prompt, no CMP
 * round trip — and replays the same `Objects` check a fresh sign-in does, so
 * a key ДПС has revoked access for still gets caught. See
 * ai/docs/kep-signing.md.
 */
export const restoreSessionFromStorage = async (
  stored: IPersistedKeySession,
): Promise<SimpleResult<ISession>> => {
  // The gateway is deployment config, not part of the persisted credential —
  // resolve it fresh so a restore always uses the current URL.
  const keyBox = createKeyBox(getCmpGatewayUrl());

  const loaded = loadKey({
    keyBox,
    file: base64ToBuffer(stored.fileBase64),
    password: stored.password,
  });

  if (!loaded.success) return castErrorResult(loaded);

  try {
    importCertificates(keyBox, stored.certificatesBase64.map(base64ToBuffer));
  } catch (importError) {
    return createErrorResultFromException(
      importError,
      ERRORS.key.certificatesNotFound,
    );
  }

  const signing = getSigningCertificate(keyBox);

  if (!signing.success || !signing.data) return castErrorResult(signing);

  return signIn({ keyBox, owner: toKeyOwner(signing.data) });
};

/**
 * Forgets the key material and the persisted copy of it, if any — the only
 * two places the decrypted key can be. See ai/docs/kep-signing.md.
 */
export const signOut = (session: ISession | null): void => {
  clearPersistedSession();

  if (!session) return;

  clearKeyBox(session.keyBox);
};

const clearKeyBox = (keyBox: IKeyBox): void => {
  keyBox.box.keys = [];
  keyBox.box.keysById = {};
};
